import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { select } from '@angular-redux/store';

import {
  ANY_TOKEN,
  NO_TOKEN,
  PERSONAL_TOKEN,
  ACCESS_TOKEN,
  DEFAULT_LOCALE,
} from '@config/index';
import { environment } from '../../environments/environment';
import {
  STATE_KEYS,
  STATE_SELECTORS,
} from '@shared/services/state-service/state-selectors';
import { Observable, observableCombineLatest, rx } from '@shared/rx';
import {
  BackendApiType,
  EHeaderName,
  ICrowdinConfig,
  ObjectId,
} from '@shared/types';
import { generateObjectId } from '@app/shared/utils';
import { StateService } from '@app/shared/services/state-service/state.service';
import { isEmpty } from 'lodash';

/**
 * Notice: this interceptor works only with absolute URLs.
 *         It means that {@link UrlInterceptor} should be provided before.
 */
@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  protected envBackendUrl: string | any = environment.xviBackendURI;
  protected envXrptUrl: string | any = environment.xrptBackendURI;
  protected envXprtUrl: string | any = environment.xprtBackendURI;
  protected envDMBackendUrl: string = environment.dmBackendURI;
  protected envAuditTrailURIUrl: string = environment.auditTrailURI;

  @select(STATE_KEYS.CURRENT_COMPANY_ID)
  private currentCompanyId$: Observable<string | undefined>;

  @select(STATE_KEYS.ACCESS_TOKEN)
  private accessToken$: Observable<string | undefined>;

  @select(STATE_KEYS.PERSONAL_TOKEN)
  private personalToken$: Observable<string | undefined>;

  private xviBaseUrls: string[];

  public constructor(public stateService: StateService) {
    this.init();
  }

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isXenialUrl(req)) return next.handle(req);

    return observableCombineLatest([
      this.currentCompanyId$,
      this.accessToken$,
      this.personalToken$,
    ]).pipe(
      rx.first(),
      rx.map(([companyId, accessToken, personalToken]): HttpRequest<any> => {
        const headers = [
          this.handleToken.bind(this, req, accessToken, personalToken),
          this.handleCompanyId.bind(this, req, companyId),
          this.handleRequestId.bind(this, req),
          this.handleLanguageParams.bind(this, req),
        ].reduce((allHeaders, fn) => fn(allHeaders), req.headers);

        return req.clone({ headers });
      }),
      rx.switchMap((request) => next.handle(request)),
    );
  }

  protected init(): void {
    this.xviBaseUrls = [
      this.envBackendUrl,
      this.envXprtUrl,
      this.envDMBackendUrl,
      this.envXrptUrl,
      this.envAuditTrailURIUrl,
    ];
  }

  /**
   * There are three possible state of {@link EHeaderName.Authorization} upon contact with the interceptor:
   *   1. There is no the header.
   *      In this case if the URL is a URL the {@link EHeaderName.Authorization} will be added.
   *   2. The headers exists and contains a "placeholder" as value.
   *      "Placeholder" means a value of next constants:
   *      - {@link NO_TOKEN}       - the {@link EHeaderName.Authorization} will be omitted for the request.
   *      - {@link PERSONAL_TOKEN} - a personal token will be assigned
   *      - {@link ACCESS_TOKEN}   - an access token will be assigned
   *                                 (the same behavior to case then the Authorization header omitted)
   *      - {@link ANY_TOKEN}      - an access will be used if it exists, personal token will be used otherwise
   *   3. The header exists and contains token.
   *      In this case the method will do nothing.
   */
  private handleToken(
    { headers }: HttpRequest<any>,
    accessToken,
    personalToken,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    const currAuthHeaderValue = headers.get(EHeaderName.Authorization);
    if (currAuthHeaderValue === NO_TOKEN) {
      return resHeaders.delete(EHeaderName.Authorization);
    }
    const hasAuthorizationHeader = headers.has(EHeaderName.Authorization);
    const isAddPersonalToken = currAuthHeaderValue === PERSONAL_TOKEN;
    const isAddAccessToken = currAuthHeaderValue === ACCESS_TOKEN;
    const isAddAnyToken = currAuthHeaderValue === ANY_TOKEN;
    const aPlaceholder =
      isAddAnyToken || isAddPersonalToken || isAddAccessToken;

    /* avoid overriding existing */
    if (hasAuthorizationHeader && !aPlaceholder) return resHeaders;

    // eslint-disable-next-line no-nested-ternary
    const token = isAddAnyToken
      ? accessToken || personalToken
      : isAddPersonalToken
        ? personalToken
        : accessToken;
    if (!token && aPlaceholder) {
      return resHeaders.delete(EHeaderName.Authorization);
    }

    return token
      ? resHeaders.set(EHeaderName.Authorization, `Bearer ${token}`)
      : resHeaders;
  }

  private handleCompanyId(
    { headers }: HttpRequest<any>,
    companyId: ObjectId | null | undefined,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    const isExists = headers.has(EHeaderName.XCompanyId); /* avoid overriding */

    return companyId && !isExists
      ? resHeaders.set(EHeaderName.XCompanyId, companyId)
      : resHeaders;
  }

  private handleRequestId(
    { url }: HttpRequest<any>,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    return url && url.includes(BackendApiType.XVI)
      ? resHeaders.set(EHeaderName.RequestId, generateObjectId())
      : resHeaders;
  }

  private handleLanguageParams(
    { url }: HttpRequest<any>,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    if (!(url && url.includes(BackendApiType.XVI))) {
      return resHeaders;
    }

    const { userLocale } = this.stateService.getDictionaryLocals();

    const newHeader = resHeaders.set(
      EHeaderName.XLocales,
      userLocale ?? DEFAULT_LOCALE,
    );

    const crowdinConfig: ICrowdinConfig = this.stateService.getByPath(
      STATE_SELECTORS.XDM_CROWDIN_CUSTOM,
    );

    if (isEmpty(crowdinConfig?.crowdin_language_code)) {
      return newHeader;
    }

    return newHeader.set(
      EHeaderName.XLocalesPrefix,
      crowdinConfig?.crowdin_language_code,
    );
  }

  /**
   * HttpClient can be used for requests to out of services. No need to send token there
   */
  private isXenialUrl({ url }: HttpRequest<any>): boolean {
    return this.xviBaseUrls.some((sample) => url.indexOf(sample) === 0);
  }
}
